import { useState } from "react";
import { Link } from "react-router-dom";
import {
  SERVICE_CATEGORIES,
  errorMessage,
  useFlaggedFeedback,
  useResolveFeedback,
  type FlaggedFeedback,
} from "../api/rfqs.js";

/**
 * The follow-up queue: every bad outcome across every request, in one place.
 *
 * It exists because a low rating that only shows on its own request detail is
 * a low rating nobody finds. Ordered oldest first on purpose — an unhappy
 * customer gets harder to win back every day, so the top of this list is the
 * most urgent call, not the newest.
 *
 * Rows LEAVE when someone marks them handled; the rating and the customer's
 * words are never altered, only the queue membership. A queue that can only
 * grow is one people stop opening.
 */
/** Mirrors the server default in internal-admin-feedback-flagged.ts. */
const QUEUE_LIMIT = 50;

export function FollowUps() {
  const { data, isPending, isError } = useFlaggedFeedback();
  const items = data?.items ?? [];

  if (isPending) return <p className="text-sm text-charcoal">Loading…</p>;
  // Only surrender the screen when there is nothing to show. This polls every
  // minute, and treating one failed background refresh as fatal would blank a
  // perfectly good queue — and discard a half-typed note with it.
  if (isError && !data) {
    return <p className="text-sm text-charcoal">Couldn&rsquo;t load the queue.</p>;
  }

  return (
    <div className="max-w-3xl">
      <h1 className="text-xl font-bold text-charcoal">Follow-ups</h1>
      <p className="mt-1 text-sm text-charcoal">
        Jobs a customer said never happened, or rated 1&ndash;2 stars. Oldest first.
      </p>

      {isError && (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Couldn&rsquo;t refresh just now — showing the last good list.
        </p>
      )}

      {items.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-charcoal/10 bg-white p-8 text-center">
          <p className="text-sm font-semibold text-charcoal">Nothing needs a call.</p>
          <p className="mt-1 text-sm text-charcoal">
            Bad outcomes land here as soon as a customer reports one.
          </p>
        </div>
      ) : (
        <ul className="mt-4 space-y-3">
          {items.map((item) => (
            <FollowUpRow key={item.rfqId} item={item} />
          ))}
        </ul>
      )}

      {items.length >= QUEUE_LIMIT && (
        <p className="mt-3 text-sm text-charcoal">
          Showing the {QUEUE_LIMIT} oldest. Clear some to see the rest &mdash; never assume this is
          everything.
        </p>
      )}
    </div>
  );
}

function FollowUpRow({ item }: { item: FlaggedFeedback }) {
  const resolve = useResolveFeedback();
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState("");

  const category =
    SERVICE_CATEGORIES.find((c) => c.value === item.serviceCategory)?.label ??
    item.serviceCategory ??
    "Request";

  return (
    <li className="rounded-2xl border border-charcoal/10 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-charcoal">
            {category}
            {item.businessName ? ` · ${item.businessName}` : ""}
          </p>
          {item.description && (
            <p className="mt-0.5 line-clamp-2 text-sm text-charcoal">{item.description}</p>
          )}
        </div>
        <span
          className={[
            "shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold",
            item.workCompleted ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-700",
          ].join(" ")}
        >
          {item.workCompleted ? `${item.rating ?? "?"}/5` : "never happened"}
        </span>
      </div>

      {item.comment && (
        <p className="mt-2 whitespace-pre-wrap rounded-lg bg-field-gray p-3 text-sm text-charcoal">
          &ldquo;{item.comment}&rdquo;
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-charcoal">
        <span>Reported {new Date(item.submittedAt).toLocaleDateString()}</span>
        {item.source === "staff" && <span>· taken by staff</span>}
        <Link to={`/customers/${item.rfqId}`} className="font-semibold text-brand-700 hover:underline">
          Open request
        </Link>
      </div>

      {confirming ? (
        <div className="mt-3 space-y-2 rounded-xl bg-field-gray p-3">
          <label className="block">
            <span className="text-xs font-semibold text-charcoal">
              What happened? (recorded, optional)
            </span>
            <input
              type="text"
              value={note}
              maxLength={500}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. called them, sending a different pro"
              className="mt-1 w-full rounded-lg border border-charcoal/20 px-3 py-2 text-sm"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={resolve.isPending}
              onClick={() =>
                resolve.mutate({
                  rfqId: item.rfqId,
                  seenAt: item.submittedAt,
                  ...(note.trim() ? { note: note.trim() } : {}),
                })
              }
              className="rounded-lg bg-brand-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {resolve.isPending ? "Clearing…" : "Confirm"}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="rounded-lg border border-charcoal/20 px-3 py-2 text-sm font-semibold text-charcoal"
            >
              Cancel
            </button>
          </div>
          {resolve.error && (
            <p className="text-sm text-rose-700">{errorMessage(resolve.error)}</p>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="mt-3 rounded-lg border border-brand-700 px-3 py-1.5 text-xs font-semibold text-brand-700"
        >
          Mark handled
        </button>
      )}
    </li>
  );
}
